import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import CollapsibleCard from "./CollapsibleCard";
import { fetchJson } from "../lib/api";
import { apiBase } from "../lib/apiBase";
import useWhaleSummary, { INTENSITY_ORDER } from "../hooks/useWhaleSummary";

const DEFAULT_MULTIPLIER = 1.0;
const DEFAULT_RANGE = { min: 0.5, max: 1.5 };

function intensityRank(value = "") {
    const idx = INTENSITY_ORDER.indexOf(String(value || "").toUpperCase());
    return idx === -1 ? 0 : idx;
}

function formatTimestamp(ts) {
    if (!ts) return "–";
    try {
        return new Date(ts).toLocaleTimeString();
    } catch {
        return ts;
    }
}

function coinLabel(value) {
    if (!value) return "—";
    return String(value).toUpperCase();
}

export default function WhaleActivityPanel({
    className = "",
    pollIntervalMs = 20000,
    authenticated = true,
    onUnauthorized = () => {},
}) {
    const [displayEnabled, setDisplayEnabled] = useState(true);
    const [sliderValue, setSliderValue] = useState(100);
    const [pinModalOpen, setPinModalOpen] = useState(false);
    const [pinValue, setPinValue] = useState("");
    const [pinError, setPinError] = useState("");
    const [pendingValue, setPendingValue] = useState(null);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const displayInitializedRef = useRef(false);
    const [sliderBaseline, setSliderBaseline] = useState(100);
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState("");
    const [saveStatus, setSaveStatus] = useState("idle");
    const [saveError, setSaveError] = useState("");
    const [multiplierRange, setMultiplierRange] = useState(DEFAULT_RANGE);
    const lastSavedRef = useRef(100);
    const configReadyRef = useRef(false);
    const mountedRef = useRef(true);
    const onUnauthorizedRef = useRef(onUnauthorized);

    const resolveEnv = useCallback(() => {
        if (typeof globalThis !== "undefined" && globalThis.__IMPORT_META_ENV__) {
            return globalThis.__IMPORT_META_ENV__;
        }
        if (typeof window !== "undefined" && window.__IMPORT_META_ENV__) {
            return window.__IMPORT_META_ENV__;
        }
        return {};
    }, []);

    const configuredPin = useMemo(() => {
        const runtimeEnv = resolveEnv();
        const envPin = (runtimeEnv?.VITE_SAFETY_CONFIRM_PIN ?? "").trim();
        if (envPin) return envPin;
        const legacyPin = (typeof globalThis !== "undefined" && globalThis.__SAFETY_CONFIRM_PIN__) || "";
        return String(legacyPin || "").trim();
    }, [resolveEnv]);
    const pinRequired = configuredPin.length > 0;

    useEffect(() => {
        onUnauthorizedRef.current = onUnauthorized;
    }, [onUnauthorized]);

    const handleUnauthorized = useCallback(() => {
        if (onUnauthorizedRef.current) {
            onUnauthorizedRef.current();
        }
    }, []);

    const { alerts, biases, lastUpdated, influenceEnabled, loading, error } = useWhaleSummary({
        authenticated,
        pollIntervalMs,
        onUnauthorized: handleUnauthorized,
    });

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        [],
    );

    useEffect(() => {
        if (!authenticated) {
            setConfigLoading(false);
            setConfigError("Sign in required");
            return;
        }

        const loadConfig = async () => {
            setConfigLoading(true);
            try {
                const res = await fetchJson(apiBase("whale_influence_config"));
                if (res?.unauth) {
                    setConfigError("Sign in required");
                    handleUnauthorized();
                    return;
                }
                if (!res?.ok || !res.data || typeof res.data !== "object") {
                    setConfigError("Unable to load whale influence settings.");
                    return;
                }
                const data = res.data || {};
                if (data.ok === false || data.status === false || !Number.isFinite(Number(data.sensitivity_multiplier))) {
                    setConfigError("Unable to load whale influence settings.");
                    return;
                }
                const min = Number.isFinite(Number(data.min)) ? Number(data.min) : DEFAULT_RANGE.min;
                const max = Number.isFinite(Number(data.max)) ? Number(data.max) : DEFAULT_RANGE.max;
                const safeMin = Math.max(DEFAULT_RANGE.min, min);
                const safeMax = Math.max(safeMin, max);
                const loadedMultiplier = Number(data.sensitivity_multiplier);
                if (!Number.isFinite(loadedMultiplier) || loadedMultiplier < safeMin || loadedMultiplier > safeMax) {
                    setConfigError("Unable to load whale influence settings.");
                    return;
                }
                const sliderVal = Math.round(
                    Math.min(safeMax, Math.max(safeMin, data.sensitivity_multiplier ?? DEFAULT_MULTIPLIER)) * 100,
                );
                setMultiplierRange({ min: safeMin, max: safeMax });
                lastSavedRef.current = sliderVal;
                setSliderBaseline(sliderVal);
                setSliderValue(sliderVal);
                configReadyRef.current = true;
                setConfigError("");
            } catch {
                setConfigError("Unable to load whale influence settings.");
            } finally {
                setConfigLoading(false);
            }
        };

        loadConfig();
    }, [authenticated, handleUnauthorized]);

    const clampMultiplier = useCallback(
        (value) => {
            const min = Number(multiplierRange.min ?? DEFAULT_RANGE.min);
            const max = Number(multiplierRange.max ?? DEFAULT_RANGE.max);
            const numeric = Number.isFinite(value) ? Number(value) : DEFAULT_MULTIPLIER;
            return Math.min(max, Math.max(min, numeric));
        },
        [multiplierRange.max, multiplierRange.min],
    );

    const sliderFromMultiplier = useCallback(
        (value) => Math.round(clampMultiplier(value ?? DEFAULT_MULTIPLIER) * 100),
        [clampMultiplier],
    );

    const multiplierFromSlider = useCallback(
        (value) => clampMultiplier((Number(value ?? sliderValue) || DEFAULT_MULTIPLIER) / 100),
        [clampMultiplier, sliderValue],
    );

    const currentMultiplier = useMemo(
        () => multiplierFromSlider(sliderValue),
        [multiplierFromSlider, sliderValue],
    );

    const minIntensity = useMemo(() => {
        if (currentMultiplier > 1.1) return "HIGH";
        if (currentMultiplier >= 0.9) return "MEDIUM";
        return "LOW";
    }, [currentMultiplier]);

    useEffect(() => {
        if (!displayInitializedRef.current && !configLoading) {
            setDisplayEnabled(influenceEnabled);
            setSliderBaseline(sliderValue);
            lastSavedRef.current = sliderValue;
            displayInitializedRef.current = true;
        }
    }, [configLoading, influenceEnabled, sliderValue]);

    const validateSliderValue = useCallback(
        (nextValue, rangeOverride = multiplierRange) => {
            const numericSlider = Number(nextValue);
            const minSlider = Math.round((rangeOverride.min ?? DEFAULT_RANGE.min) * 100);
            const maxSlider = Math.round((rangeOverride.max ?? DEFAULT_RANGE.max) * 100);
            if (!Number.isFinite(numericSlider) || numericSlider < minSlider || numericSlider > maxSlider) {
                setSaveStatus("error");
                setSaveError("Invalid whale sensitivity value.");
                return false;
            }
            return true;
        },
        [multiplierRange.max, multiplierRange.min],
    );

    const submitSensitivity = useCallback(
        async (nextValue) => {
            if (!validateSliderValue(nextValue)) return;
            const numericSlider = Number(nextValue);

            const previousSlider = lastSavedRef.current;
            const multiplier = multiplierFromSlider(numericSlider);
            setSaveStatus("saving");
            setSaveError("");
            try {
                const res = await fetchJson(apiBase("whale_influence_config"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sensitivity_multiplier: multiplier }),
                });
                if (res?.unauth) {
                    if (mountedRef.current) {
                        setSliderValue(previousSlider);
                        setSaveStatus("error");
                        setSaveError("Sign in required to update whale sensitivity.");
                        handleUnauthorized();
                    }
                    return;
                }
                const data = res?.data;
                const confirmedMultiplier = Number(data?.sensitivity_multiplier);
                if (!res?.ok || !data || data.ok === false || data.status === false || !Number.isFinite(confirmedMultiplier)) {
                    if (mountedRef.current) {
                        setSliderValue(previousSlider);
                        setSaveStatus("error");
                        setSaveError("Whale sensitivity update failed; backend did not confirm the change.");
                    }
                    return;
                }
                const nextSlider = sliderFromMultiplier(confirmedMultiplier);
                lastSavedRef.current = nextSlider;
                if (mountedRef.current) {
                    setSliderBaseline(nextSlider);
                    setSliderValue(nextSlider);
                    setSaveStatus("saved");
                    setSaveError("");
                    setTimeout(() => {
                        if (mountedRef.current) setSaveStatus("idle");
                    }, 1200);
                }
            } catch {
                if (mountedRef.current) {
                    setSliderValue(previousSlider);
                    setSaveStatus("error");
                    setSaveError("Whale sensitivity update failed; backend unavailable.");
                }
            }
        },
        [handleUnauthorized, multiplierFromSlider, sliderFromMultiplier, validateSliderValue],
    );

    const alertCounts = useMemo(() => {
        const map = new Map();
        alerts.forEach((a) => {
            const key = (a?.coin || "").toString().toLowerCase();
            if (!key) return;
            map.set(key, (map.get(key) || 0) + 1);
        });
        return map;
    }, [alerts]);

    const visibleBiases = useMemo(() => {
        const filtered = biases
            .filter((b) => intensityRank(b.intensity) >= intensityRank(minIntensity))
            .sort((a, b) => intensityRank(b.intensity) - intensityRank(a.intensity));
        return displayEnabled ? filtered : [];
    }, [biases, displayEnabled, minIntensity]);

    const statusText = useMemo(() => {
        if (!authenticated) return "Sign in to view";
        if (error === "unauthorized") return "Sign in required";
        if (configLoading) return "Loading";
        if (configError) return "Settings unavailable";
        if (error) return "Unavailable";
        if (loading) return "Loading";
        if (!influenceEnabled) return "Influence disabled";
        if (visibleBiases.length === 0) return "No whale signals";
        return `${visibleBiases.length} active signals`;
    }, [authenticated, configError, configLoading, error, influenceEnabled, loading, visibleBiases.length]);

    const sliderStatus = useMemo(() => {
        if (configLoading) return "Loading…";
        if (configError || saveStatus === "error") return "Error";
        if (saveStatus === "saving") return "Saving…";
        if (saveStatus === "saved") return "Saved";
        return "";
    }, [configError, configLoading, saveStatus]);

    const sliderStatusColor = useMemo(() => {
        if (sliderStatus === "Error") return "text-red-300";
        if (sliderStatus === "Saved") return "text-green-300";
        if (sliderStatus === "Saving…") return "text-cyan-200";
        return "text-gray-400";
    }, [sliderStatus]);

    const resetPinModal = useCallback(() => {
        setPinModalOpen(false);
        setPinValue("");
        setPinError("");
    }, []);

    const validatePin = useCallback(
        (value) => {
            if (!pinRequired) return { ok: true };
            const normalized = String(value ?? "").trim();
            if (normalized.length !== configuredPin.length) {
                return { ok: false, error: `PIN must be a ${configuredPin.length}-digit code.` };
            }
            if (normalized !== configuredPin) {
                return { ok: false, error: "Incorrect PIN. Please try again." };
            }
            return { ok: true };
        },
        [configuredPin, pinRequired],
    );

    const startPinFlow = useCallback(
        (nextValue) => {
            if (!pinRequired) {
                submitSensitivity(nextValue);
                return;
            }
            setPendingValue(nextValue);
            setPinModalOpen(true);
            setPinValue("");
            setPinError("");
        },
        [pinRequired, submitSensitivity],
    );

    const handleConfirmPin = useCallback(() => {
        const result = validatePin(pinValue);
        if (!result.ok) {
            setPinError(result.error || "Incorrect PIN. Please try again.");
            return;
        }
        setIsUnlocked(true);
        if (pendingValue !== null) {
            submitSensitivity(pendingValue);
            setPendingValue(null);
        }
        resetPinModal();
    }, [pendingValue, pinValue, resetPinModal, submitSensitivity, validatePin]);

    const handleCancelPin = useCallback(() => {
        setPendingValue(null);
        setSliderValue(sliderBaseline);
        resetPinModal();
    }, [resetPinModal, sliderBaseline]);

    useEffect(() => {
        if (!pinModalOpen) return undefined;
        const handler = (event) => {
            if (event.key === "Escape") {
                handleCancelPin();
            }
            if (event.key === "Enter") {
                handleConfirmPin();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [handleCancelPin, handleConfirmPin, pinModalOpen]);

    const intensityBadge = (level) => {
        const palette = {
            LOW: "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
            MEDIUM: "bg-amber-500/10 text-amber-200 border-amber-500/40",
            HIGH: "bg-red-500/10 text-red-200 border-red-500/40",
        };
        const text = (level || "").toString().toUpperCase();
        return (
            <span className={`px-2 py-1 rounded-full border text-[11px] font-semibold ${palette[text] || "border-gray-700"}`}>
                {text || "UNKNOWN"}
            </span>
        );
    };

    const renderBody = () => {
        if (!authenticated || error === "unauthorized") {
            return (
                <div className="flex items-center gap-2 text-xs text-amber-200" data-testid="whale-activity-unauthorized">
                    <span className="inline-block h-2 w-2 rounded-full bg-amber-400" aria-hidden />
                    Sign in or provide an API token to view whale activity.
                </div>
            );
        }
        if (configError) {
            return <div className="text-xs text-red-300">{configError}</div>;
        }
        if (saveError) {
            return (
                <div className="text-xs text-red-300" data-testid="whale-sensitivity-error">
                    {saveError}
                </div>
            );
        }
        if (error && error !== "unauthorized") {
            return (
                <div className="text-xs text-red-300" data-testid="whale-activity-error">
                    Unable to load whale signals. Check network/API access.
                </div>
            );
        }

        if (!displayEnabled) {
            return (
                <div className="text-xs text-gray-300" data-testid="whale-activity-muted">
                    Whale influence display is paused. Re-enable to surface whale bias hints.
                </div>
            );
        }

        if (visibleBiases.length === 0) {
            return (
                <div className="text-xs text-gray-400" data-testid="whale-activity-empty">
                    No whale events recorded yet. Waiting for the first alert.
                </div>
            );
        }

        return (
            <ul className="space-y-2" data-testid="whale-activity-list">
                {visibleBiases.map((bias) => (
                    <li
                        key={bias.coin}
                        className="rounded border border-gray-800 bg-gray-900/60 p-2 text-xs"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-white">{coinLabel(bias.coin)}</span>
                                {intensityBadge(bias.intensity)}
                            </div>
                            <span className="text-gray-400">{formatTimestamp(bias.last_updated)}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-gray-300">
                            <span>
                                Bias: {Number(bias.whale_bias ?? 0).toFixed(2)} ({bias.config_enabled ? "active" : "muted"})
                            </span>
                            <span className="text-gray-400">
                                Alerts: {alertCounts.get((bias.coin || "").toLowerCase()) || 0}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <>
            {pinModalOpen && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60" role="dialog">
                    <div className="w-full max-w-sm rounded-2xl border border-gray-800 bg-gray-900 p-5 shadow-xl">
                        <div className="mb-3 space-y-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">Security</p>
                            <h4 className="text-lg font-semibold text-white">Enter PIN to adjust sensitivity</h4>
                            <p className="text-xs text-gray-400">
                                Sensitive controls are protected. Confirm the session PIN to continue.
                            </p>
                        </div>
                        <input
                            autoFocus
                            data-testid="pin-input"
                            className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none"
                            placeholder="Enter PIN"
                            value={pinValue}
                            onChange={(e) => {
                                setPinValue(e.target.value || "");
                                if (pinError) setPinError("");
                            }}
                            type="password"
                            inputMode="numeric"
                        />
                        {pinError && <div className="mt-2 text-xs text-red-300">{pinError}</div>}
                        <div className="mt-4 flex items-center justify-end gap-2 text-xs font-semibold">
                            <button
                                type="button"
                                className="rounded border border-gray-700 bg-gray-800 px-3 py-1 text-gray-200 hover:border-gray-500"
                                onClick={handleCancelPin}
                                data-testid="pin-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="rounded border border-cyan-500 bg-cyan-600/20 px-3 py-1 text-cyan-100 hover:bg-cyan-600/30"
                                onClick={handleConfirmPin}
                                data-testid="pin-confirm"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CollapsibleCard
                title="Whale Activity"
                subtitle="Pulse signal"
                className={`border border-gray-800 bg-gray-900/70 ${className}`}
                indicator={<span className="text-[11px] text-gray-300">{statusText}</span>}
                bodyClassName="space-y-3"
                testId="whale-activity-panel"
                showToggle={false}
            >
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-300">
                <div className="flex items-center gap-2">
                    <span className="text-gray-400">Alerts (1h)</span>
                    <span className="rounded bg-gray-800 px-2 py-1 font-semibold text-white">
                        {alerts.length}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-gray-400">Influence</span>
                    <span className={influenceEnabled ? "text-green-300" : "text-red-300"}>
                        {influenceEnabled ? "Enabled" : "Disabled"}
                    </span>
                </div>
                <div className="text-gray-400">Updated: {formatTimestamp(lastUpdated)}</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-200" data-testid="whale-activity-controls">
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={displayEnabled}
                        onChange={(e) => setDisplayEnabled(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-700 bg-gray-800"
                    />
                    <span className="text-gray-300">Show whale influence hints</span>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">Sensitivity</span>
                    <input
                        type="range"
                        min={Math.round((multiplierRange.min ?? DEFAULT_RANGE.min) * 100)}
                        max={Math.round((multiplierRange.max ?? DEFAULT_RANGE.max) * 100)}
                        step="5"
                        value={sliderValue}
                        onChange={(e) => {
                            const rawNext = Number(e.target.value);
                            if (!validateSliderValue(e.target.value)) return;
                            const next = rawNext;
                            if (isUnlocked || !pinRequired) {
                                submitSensitivity(next);
                                return;
                            }
                            startPinFlow(next);
                        }}
                        className="accent-cyan-400"
                        data-testid="whale-sensitivity-slider"
                        disabled={configLoading || Boolean(configError) || saveStatus === "saving"}
                    />
                    <div className="flex items-center justify-between text-[11px] text-gray-400">
                        <span>Highlighting {minIntensity}+</span>
                        {sliderStatus && (
                            <span className={sliderStatusColor} data-testid="whale-sensitivity-status">
                                {sliderStatus}
                            </span>
                        )}
                    </div>
                </label>
                <div className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">Dashboard impact</span>
                    <p className="text-[11px] text-gray-300">
                        Adjusts whale influence sensitivity across engines. Changes affect backend sizing within existing safety caps.
                    </p>
                </div>
            </div>

            {renderBody()}
        </CollapsibleCard>
        </>
    );
}

WhaleActivityPanel.propTypes = {
    className: PropTypes.string,
    pollIntervalMs: PropTypes.number,
    authenticated: PropTypes.bool,
    onUnauthorized: PropTypes.func,
};
